import React from 'react';
import styled, { css } from 'styled-components';
import type { HTMLAttributes } from 'react';
import type { ThemeProps } from 'styled-components';
import type { AllColorType, SizeType, ThemeType } from '../../../util/types';
import Tooltip from '../../tooltip/Tooltip';
import { DEFAULT_TRANSITION_DURATION, DEFAULT_TRANSITION_FUNC, getPolishedColors } from '../../../util/styled';

type ValueType = {
  value: number;
  color: AllColorType;
  message: string;
};

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends HTMLProps {
  /**
   * Chart identification.
   */
  id: string;
  /**
   * Total value of the chart. Sample value.
   */
  total: number;
  /**
   * Object with the values and colors of both elements.
   * Values distribution is based on total value.
   */
  values: {
    a: ValueType;
    b: ValueType;
  };
  /**
   * Sets border color for the main container as well for both elements.
   */
  borderColor?: AllColorType;
  /**
   * Sets chart height based on theme sizes.
   */
  size?: SizeType;
}

const CompareProgressBar = React.forwardRef<Ref, Props>(
  ({ id, total, values, borderColor = 'secondary', size = 'sm', ...other }: Props, ref) => {
    return (
      <ProgressWrapperStyled ref={ref} $size={size} $borderColor={borderColor} {...(other as HTMLProps)}>
        {Object.entries(values).map((value) => {
          if (value[1].value > 0) {
            return (
              <Tooltip key={`${id}_bar--${value[0]}`} tooltipMessage={value[1].message}>
                <BarStyled
                  key={`${id}_bar--${value[0]}`}
                  $color={value[1].color}
                  $borderColor={borderColor}
                  $value={value[1].value}
                  $total={total}
                />
              </Tooltip>
            );
          }
        })}
        {values.a.value + values.b.value !== total && (
          <BarStyled $color={'light'} $total={total} $value={total - (values.a.value + values.b.value)} />
        )}
      </ProgressWrapperStyled>
    );
  },
);

const ProgressWrapperStyled = styled('div')<{ $size: SizeType; $borderColor: AllColorType }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: start;
  overflow: hidden;
  border-radius: 9999px;
  border: 1px solid ${(props) => props.theme.colors[props.$borderColor]};

  width: 100%;
  height: 16px;

  background: ${(props) => props.theme.colors.light};
`;

const getBarColor = (props: ThemeProps<ThemeType> & { $color: AllColorType }) => {
  const colors = getPolishedColors(props);
  return css`
    background: ${colors.main.color};
    &:hover {
      background: ${colors.light.color};
    }
  `;
};

const BarStyled = styled('div')<{ $color: AllColorType; $borderColor?: AllColorType; $value: number; $total: number }>`
  position: relative;
  width: calc(${(props) => (props.$value * 100) / props.$total}% + 15px);
  height: 100%;
  transition: background ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};

  &:not(:last-child) {
    border-right: 1px solid ${(props) => (props.$borderColor ? props.theme.colors[props.$borderColor] : 'transparent')};
  }

  ${(props) => getBarColor(props)}
`;

CompareProgressBar.displayName = 'CompareProgressBar';

export default CompareProgressBar;
